import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { Booth } from 'models/cms/booths';

import { FETCH_ASSETS_URL } from 'appenv';
import BoothContainer from '../../components/BoothContainer';
import OrangeShelf from '../../components/brochureShelf/OrangeShelf';
import GenericCMSBoothBanner from '../../components/GenericCMSBoothBanner';
import BoothSaleContainer from '../BoothSaleContainer';
import LogoDesk from '../../components/desk/LogoDesk';
import BoothBackground from './BoothBackground';

const leftBannerSvg = `${FETCH_ASSETS_URL}/assets/booths/cuhk925/left_banner.svg`;
const rightBannerSvg = `${FETCH_ASSETS_URL}/assets/booths/cuhk925/right_banner.svg`;

const TopBannerContainer = styled.div`
  position: absolute;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 315px;
  height: 190px;
  top: 245px;
  left: calc((90% - 315px) / 2);
  z-index: 2;
`;

const TopBannerWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const ShowDeskContainer = styled.div`
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 450px;
  width: 300px;
  left: calc((90% - 300px) / 2);
  z-index: 2;
`;

const BackgroundContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: -50px;
`;

const LeftBannerBgContainer = styled.div<{ bg: any }>`
  width: 200px;
  height: 430px;
  padding: 6.4px 9px 9px;
  position: absolute;
  bottom: -83px;
  left: -132px;
  z-index: 2;
  background: url(${(props) => props.bg}) no-repeat;
`;

const LeftBannerContainer = styled.div`
  height: 378px;
  background-color: #FFF;
  width: 172px;
  left: 6px;
  position: relative;
`;

const DisplayBoardBgContainer = styled.div<{ bg: any }>`
  width: 170px;
  height: 318px;
  padding: 6.4px 9px 9px;
  position: absolute;
  bottom: -30px;
  left: 68px;
  z-index: 2;
  background: url(${(props) => props.bg}) no-repeat;
`;

const DisplayBoardContainer = styled.div`
  width: 154px;
  height: 200px;
  position: relative;
  background-color: #FFF;
  left: -4px;
`;

const BrochureContainer = styled.div<{ enableClick: boolean }>`
  bottom: -90px;
  right: 195px;
  cursor: ${({ enableClick }) => (enableClick ? 'pointer' : 'initial')};
  position: absolute;
  width: 125px;
  z-index: 2;
`;

const SaleContainer = styled.div`
  position: absolute;
  width: 200px;
  height: 480px;
  right: -80px;
  bottom: -100px;
  z-index: 2;
`;

const CenterBannerContainer = styled.div`
  position: relative;
  transform: scale(0.7, 0.7);
  left: -35px;
`;

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 900px;
`;

export interface BannerProps {
  booth: Booth;
  handleClick: (type: string, isCMSElement: boolean) => void;
  isPreview?: boolean;
}

const Banner: React.FC<BannerProps> = ({
  booth,
  handleClick = () => null,
  isPreview = false,
}: BannerProps) => {
  const {
    leftColor, rightColor,
    leftBanner, displayBoard, topBanner, centerBanner,
  } = useMemo(() => {
    const elements = (booth.designElements || []);
    return ({
      leftColor: elements.find((designNode) => designNode.name === 'left_color')?.color,
      rightColor: elements.find((designNode) => designNode.name === 'right_color')?.color,
      leftBanner: elements.find((designNode) => designNode.name === 'left_banner'),
      displayBoard: elements.find((designNode) => designNode.name === 'display_board'),
      topBanner: elements.find((designNode) => designNode.name === 'top_banner'),
      centerBanner: elements.find((designNode) => designNode.name === 'middle_banner'),
    });
  }, [booth.designElements]);
  const showRightRepresentative = useMemo(() => !isPreview, [isPreview]);
  const { themeColor } = useSelector((s: any) => s.eventConfigs);

  return (
    <RootContainer>
      <BoothContainer needOffset={!isPreview}>
        <BackgroundContainer>
          <BoothBackground leftColor={leftColor || rightColor || themeColor} rightColor={rightColor || leftColor || themeColor} />
        </BackgroundContainer>
        <TopBannerContainer>
          <TopBannerWrapper onClick={() => handleClick(topBanner.name, true)}>
            <GenericCMSBoothBanner
              autoPlayVideo={!isPreview}
              bannerData={topBanner}
              onBannerClick={handleClick as any}
            />
          </TopBannerWrapper>
        </TopBannerContainer>
        <ShowDeskContainer>
          <CenterBannerContainer onClick={() => handleClick(centerBanner.name, true)}>
            <LogoDesk
              deskColor="#484848"
              bannerData={centerBanner}
              showShadow={false}
              handleClick={handleClick}
            />
          </CenterBannerContainer>
        </ShowDeskContainer>
        <LeftBannerBgContainer bg={leftBannerSvg}>
          <LeftBannerContainer onClick={() => handleClick(leftBanner.name, true)}>
            <GenericCMSBoothBanner
              bannerData={leftBanner}
              autoPlayVideo={!isPreview}
              onBannerClick={handleClick as any}
            />
          </LeftBannerContainer>
        </LeftBannerBgContainer>
        <DisplayBoardBgContainer bg={rightBannerSvg}>
          <DisplayBoardContainer onClick={() => handleClick(displayBoard.name, true)}>
            <GenericCMSBoothBanner
              bannerData={displayBoard}
              autoPlayVideo={!isPreview}
              onBannerClick={handleClick as any}
            />
          </DisplayBoardContainer>
        </DisplayBoardBgContainer>
        <BrochureContainer enableClick={booth.hasAttachment} onClick={() => handleClick('attachment', null)}>
          <OrangeShelf hasAttachment={booth.hasAttachment} themeColor={themeColor} />
        </BrochureContainer>
        {
          showRightRepresentative && (
            <SaleContainer id="reps">
              <BoothSaleContainer booth={booth} sale="male-right" />
            </SaleContainer>
          )
        }
      </BoothContainer>
    </RootContainer>
  );
};

export default Banner;
